import CloseIcon from '@mui/icons-material/Close';
import ContrastOutlinedIcon from '@mui/icons-material/ContrastOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MultiplexPanelChannel } from 'interfaces/multiplexPanels';
import { map } from 'lodash';
import React, { useState } from 'react';
import { HScoreBinsEditor } from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/sections/InferenceModelsSection/HScoreBinsEditor';
import { getHScoreBinMaximumDrafts } from '../../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/sections/InferenceModelsSection/useHScoreBins';
import type { PanelConfigurationDraft } from './PanelConfigurationEditor';

export const PanelPositivityTable = ({
  channels,
  displayNames,
  draft,
  onCutoffChange,
  onLinkChange,
  onBinsChange,
  onInheritBins,
  hScoresEnabled = true,
  headingAction,
}: {
  channels: MultiplexPanelChannel[];
  displayNames: Record<string, string>;
  draft: PanelConfigurationDraft;
  onCutoffChange: (probeId: string, value: string) => void;
  onLinkChange: (probeId: string, linked: boolean) => void;
  onBinsChange: (probeId: string, maximums: string[]) => void;
  onInheritBins: (probeId: string) => void;
  hScoresEnabled?: boolean;
  headingAction?: React.ReactNode;
}) => {
  const [editing, setEditing] = useState<{ probeId: string; anchor: HTMLElement } | null>(null);
  const editedBins = editing
    ? draft.perProbeSetup[editing.probeId]?.countBins ?? draft.spotDetectionSetup.countBins ?? []
    : [];
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <ContrastOutlinedIcon color="action" />
        <Typography variant="h3" component="h3">
          {hScoresEnabled ? 'Positivity & H-score bins' : 'Marker & probe positivity'}
        </Typography>
        {headingAction}
      </Stack>
      <TableContainer>
        <Table size="small" aria-label="Marker and probe positivity">
          <TableHead>
            <TableRow>
              <TableCell>Marker / probe</TableCell>
              <TableCell>Signal source</TableCell>
              <TableCell>Positive from</TableCell>
              <TableCell>H-score bins</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(channels, (channel) => {
              const probeId = channel.analysisName;
              const isRna = channel.channelModality === 'rna';
              const name = displayNames[probeId];
              const linked = draft.probeThresholdLinks?.[probeId] === true;
              const value = isRna ? draft.probeThresholds[probeId] ?? '' : draft.markerThresholds[probeId] ?? '';
              const valid =
                value.trim() !== '' &&
                Number.isFinite(Number(value)) &&
                Number(value) >= (linked ? 1 : 0) &&
                (!linked || Number.isInteger(Number(value))) &&
                (isRna || Number(value) <= 1);
              const bins = draft.perProbeSetup[probeId]?.countBins ?? draft.spotDetectionSetup.countBins ?? [];
              return (
                <TableRow key={probeId}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{name}</Typography>
                      <Chip size="small" label={isRna ? 'RNA' : 'mIF'} />
                    </Stack>
                  </TableCell>
                  <TableCell>{isRna ? 'Spot count' : 'Classifier score'}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={value}
                      inputProps={{
                        min: linked ? 1 : 0,
                        step: isRna ? (linked ? 1 : 'any') : 0.01,
                        'aria-label': `${name} ${
                          isRna ? 'positivity threshold (spots)' : 'classifier cutoff override'
                        }`,
                      }}
                      error={isRna ? !valid : value !== '' && !valid}
                      helperText={
                        isRna
                          ? !valid
                            ? linked
                              ? 'Enter a whole count of at least 1.'
                              : 'Enter a finite non-negative count.'
                            : linked
                            ? 'Synced with Score 1'
                            : 'Independent cutoff'
                          : 'Saved cutoff used when this run does not estimate cutoffs'
                      }
                      InputProps={{
                        endAdornment:
                          isRna && hScoresEnabled ? (
                            <InputAdornment position="end">
                              <Tooltip title="Sync positivity with the first positive H-score bin">
                                <Checkbox
                                  size="small"
                                  checked={linked}
                                  inputProps={{ 'aria-label': `${name}: Follow first positive bin` }}
                                  onChange={(_event, checked) => onLinkChange(probeId, checked)}
                                />
                              </Tooltip>
                            </InputAdornment>
                          ) : undefined,
                      }}
                      onChange={(event) => onCutoffChange(probeId, event.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    {isRna && hScoresEnabled ? (
                      <Button
                        color="secondary"
                        size="small"
                        variant="outlined"
                        startIcon={<TuneIcon />}
                        aria-label={`Edit ${name} H-score bins`}
                        onClick={(event) => setEditing({ probeId, anchor: event.currentTarget })}
                      >
                        {map(bins, (bin) => `${bin.minCount}+`).join(' / ')}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isRna ? 'Not requested' : 'Not configured'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={Boolean(editing)}
        anchorEl={editing?.anchor}
        onClose={() => setEditing(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {editing && (
          <Stack
            role="dialog"
            aria-label={`${displayNames[editing.probeId]} H-score bins`}
            spacing={1}
            sx={{ p: 2, width: 640, maxWidth: '100%' }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">{displayNames[editing.probeId]} H-score bins</Typography>
              <IconButton aria-label="Close H-score bins" onClick={() => setEditing(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <HScoreBinsEditor
              countBins={editedBins}
              maximumDrafts={draft.hScoreBinMaximumDrafts?.[editing.probeId] ?? getHScoreBinMaximumDrafts(editedBins)}
              onMaximumDraftsChange={(maximums) => onBinsChange(editing.probeId, maximums)}
              editableFirstMinimum
              showPositivityThreshold={false}
              onChange={() => undefined}
              onPositivityThresholdScoreChange={() => undefined}
            />
            <Stack direction="row" justifyContent="space-between">
              <Button
                color="secondary"
                disabled={!draft.perProbeSetup[editing.probeId]}
                onClick={() => onInheritBins(editing.probeId)}
              >
                Use panel bins
              </Button>
              <Button color="inherit" onClick={() => setEditing(null)}>
                Close
              </Button>
            </Stack>
          </Stack>
        )}
      </Popover>
    </>
  );
};
